import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SectorActivity } from './Interfaces/SectorActivity';
import { Sector } from './Interfaces/Sector';
import { createAppPreAsyncThunk } from '../../../../common/thunk';
import SectorsService from '../../../../services/sectors.service';
import SpecialtiesService from '../../../../services/specialties.service';
import { SectorPayload } from './Interfaces/SectorPayload';
import { ADD_NEW_SECTOR } from './Constants/constants';
import { Option } from '../../../Common/SelectCustom/SelectCustom';
import { UpdateSectorPayload } from './Interfaces/UpdateSectorPayload';
import { Specialty } from './Interfaces/Specialty';

interface SectorsManagementState {
  sectorsActivitiesList: Array<Option>;
  currentSector?: Sector;
  currentSectorSpecialties: Array<Specialty>;
  selectedSector: Option;
  defaultSectorsOptions: Array<Option>;
  activityTypeOptions: Array<Option>;
}

const initialState: SectorsManagementState = {
  activityTypeOptions: [
    { value: 'Sport & Health', label: 'Sport & Health' },
    { value: 'Other', label: 'Other' },
  ],
  sectorsActivitiesList: [],
  defaultSectorsOptions: [{ value: ADD_NEW_SECTOR, label: '+ Add new sector' }],
  selectedSector: { value: ADD_NEW_SECTOR, label: '+ Add new sector' },
  currentSectorSpecialties: [],
};

export const getSectorsActivitiesList = createAppPreAsyncThunk('sectors/getSectorsActivitiesList', async keyword => {
  return await SectorsService.search(keyword).then(response => response.data);
});

export const createSector = createAppPreAsyncThunk('sectors/create', async (payload: SectorPayload) => {
  return await SectorsService.create(payload).then(response => response.data);
});

export const updateSector = createAppPreAsyncThunk('sectors/update', async (payload: UpdateSectorPayload) => {
  return await SectorsService.update(payload.payload, payload.id).then(response => response.data);
});

export const deleteSector = createAppPreAsyncThunk('sectors/delete', async (id: string) => {
  return await SectorsService.delete(id).then(response => response.data);
});

export const getSectorById = createAppPreAsyncThunk('sectors/getById', async (id: string) => {
  return await SectorsService.getById(id).then(response => response.data);
});

export const deleteSpecialty = createAppPreAsyncThunk('specialty/delete', async (id: string) => {
  return await SpecialtiesService.delete(id).then(response => response.data);
});

const setSectorsActivitiesList = (state: SectorsManagementState, action: PayloadAction<Array<SectorActivity>>) => {
  state.sectorsActivitiesList = action.payload.map(item => ({ value: item.id, label: item.activityName } as Option));
};

const setCurrentSector = (state: SectorsManagementState, action: PayloadAction<Sector>) => {
  state.currentSector = action.payload;
  state.currentSectorSpecialties = action.payload.specialties;
};

const setCreatedSector = (state: SectorsManagementState, action: PayloadAction<Sector>) => {
  state.currentSector = action.payload;
  state.currentSectorSpecialties = action.payload.specialties;
  state.sectorsActivitiesList.push({ value: action.payload.id, label: action.payload.title });
  state.selectedSector = { value: action.payload.id, label: action.payload.title };
};

const setUpdatedSector = (state: SectorsManagementState, action: PayloadAction<Sector>) => {
  state.currentSector = action.payload;
  state.currentSectorSpecialties = action.payload.specialties;
  state.sectorsActivitiesList = state.sectorsActivitiesList.map(item => {
    if (item.value === action.payload.id) {
      return { ...item, label: action.payload.title };
    }
    return item;
  });
  state.selectedSector = { value: state.selectedSector.value, label: action.payload.title };
};

const deleteDeletedSector = (state: SectorsManagementState, action: PayloadAction<{ deleted: string }>) => {
  state.sectorsActivitiesList = state.sectorsActivitiesList.filter(item => item.value !== action.payload.deleted);
  state.selectedSector = state.defaultSectorsOptions[0];
  state.currentSector = undefined;
  state.currentSectorSpecialties = [];
};

const deleteDeletedSpecialty = (state: SectorsManagementState, action: PayloadAction<{ deleted: string }>) => {
  state.currentSectorSpecialties = state.currentSectorSpecialties.filter(item => item.id !== action.payload.deleted);
};

export const sectorsManagementSlice = createSlice({
  name: 'sectorsManagement',
  initialState,
  reducers: {
    setEmptyCurrentSector(state) {
      state.currentSector = undefined;
    },
    setSelectedSector(state, action: PayloadAction<Option>) {
      state.selectedSector = action.payload;
    },
    updateSpecialtiesState(state, action: PayloadAction<Specialty>) {
      state.currentSectorSpecialties = state.currentSectorSpecialties.map(item => {
        if (item.id === action.payload.id) {
          return { ...item, title: action.payload.title };
        }
        return item;
      });
    },
  },
  extraReducers: builder => {
    builder.addCase(getSectorsActivitiesList.fulfilled, setSectorsActivitiesList);
    builder.addCase(createSector.fulfilled, setCreatedSector);
    builder.addCase(deleteSector.fulfilled, deleteDeletedSector);
    builder.addCase(getSectorById.fulfilled, setCurrentSector);
    builder.addCase(updateSector.fulfilled, setUpdatedSector);
    builder.addCase(deleteSpecialty.fulfilled, deleteDeletedSpecialty);
  },
});
export const { setEmptyCurrentSector, setSelectedSector, updateSpecialtiesState } = sectorsManagementSlice.actions;
export default sectorsManagementSlice.reducer;
